import {
  MutationActions,
  useEntityMutation,
  QueryKeys,
  queryClient,
} from "@/api/core";
import { useApi } from "@/hooks";
import { statusSchema } from "@/schemas";

import { updateStatusCache } from "../../statuses/utils";
import { SuppressionQueryKeys } from "../query-keys";

import type { ApiErrorResponse } from "@/types/api";

interface Variables {
  statusId: string;
  accountId: string;
}

function useSuppressAccount() {
  const api = useApi();

  return useEntityMutation({
    globalQueryKey: QueryKeys.statuses.global,
    idAttributeFromVariables: "statusId",
    action: MutationActions.UPDATE,
    schema: statusSchema,
    skipOnSuccess: true,
    mutationFn: (variables: Variables) =>
      api.post("/api/v1/recommendations/accounts/suppressions", {
        target_account_id: variables.accountId,
        status_id: variables.statusId,
      }),
    onMutate(variables) {
      const [prevStatus] = updateStatusCache(variables.statusId, () => ({
        interested: false,
      }));

      return { prevStatus };
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: SuppressionQueryKeys.statuses,
      });
    },
    onError(error: ApiErrorResponse, variables, context) {
      // If the user has already supressed this Account, do nothing at all.
      // ie: don't reset the data on the Status, b/c it's a better UX to continue
      // to show the minimize Status w/ a message that you have supressed it.
      const isDuplicateRecord =
        error.response?.data?.error === "Duplicate record";
      if (isDuplicateRecord) {
        return;
      }

      if (context) {
        queryClient.setQueryData(
          QueryKeys.statuses.detail(variables.statusId),
          context.prevStatus,
        );
      }
    },
  });
}

export { useSuppressAccount };
